import { createSelector } from '@reduxjs/toolkit';

import { CheckResult, LoadCase, ObjectChecked } from '@paragon/contract-library';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { SelectableEntryOfType } from '../../library/selection/types';
import { RootState } from '..';
import { getActiveAnalysisSet } from './getActiveAnalysisSetSelector';
import { getAnalysisResultGuids } from './getAnalysisResultsForActiveAnalysisSetSelector';
import { getCheckedMembersByCheckResultForActiveLoadCase } from './getCheckedMembersByCheckResultForActiveLoadCase';
import { getCheckResultsForActiveLoadCaseAndItem } from './getCheckResultsForActiveLoadCaseAndItemSelector';
import { getHighestCapacityForItemUsingSummary } from './getHighestCapacityForItemSelector';
import { getLoadCasesForActiveComponentDesign } from './getLoadCasesForActiveComponentDesignSelector';
import { getSelectedCheckResultsLoadCase } from './getSelectedCheckResultsLoadCaseSelector';
import { getSingleSelectedItemEntryOrNull } from './getSelectedItemSelector';

const selectableCheckedObjectTypes = ['member', 'platePair', 'panel', 'analogJoint', 'analogLink'] as const;
type SelectableCheckedObjectType = (typeof selectableCheckedObjectTypes)[number];

const isSelectableCheckedObject = (entry: any): entry is SelectableEntryOfType<SelectableCheckedObjectType> =>
  entry != null && (selectableCheckedObjectTypes as readonly string[]).includes(entry.type);

interface Check {
  readonly tpiSection: string;
  readonly name: string;
}

// A CheckResultTreeEntry holds an item we want to be included in the check results tree and the data needed to
// determine its ancestors and descendants. The item is a check result if the necessary check results have been loaded.
// Otherwise, we create entries for nodes higher in the tree (closer to the root) where possible.
export interface CheckResultTreeEntry {
  readonly checkResult: CheckResult | null;
  readonly capacity: number;
  // The object to group check results by first in the CheckResultsPanel tree, or null if we are filtering to the
  // selected object and thus don't need this preliminary grouping.
  readonly mainObject: ObjectChecked | null;
  readonly check: Check | null;
  // Objects other than mainObject that should have grouping nodes in the CheckResultsPanel tree.
  readonly subObjects: readonly ObjectChecked[] | null;
  // The load case for the check result, or null if we are filtering results to a single load case and thus don't need a
  // load case grouping node in the tree.
  readonly loadCase: LoadCase | null;
  readonly plateMislocation: ObjectChecked | null;
}

const getCheckResultSummaryTreeEntries = createSelector(
  getActiveAnalysisSet,
  (analysisSet): readonly CheckResultTreeEntry[] | null => {
    if (!analysisSet) {
      return null;
    }
    const {
      componentDesign: { analogModel, component },
      objectCapacities,
    } = analysisSet;
    if (!analogModel || !objectCapacities) {
      return null;
    }
    type EntryData = Readonly<{ objectChecked: ObjectChecked; capacity: number }>;
    const memberEntryData = component.members
      .map((member): EntryData | null => {
        const capacity = getHighestCapacityForItemUsingSummary(analysisSet, member.guid, 'member');
        if (capacity == null) return null;
        return {
          objectChecked: { guid: member.guid, name: member.name, type: member.memberType },
          capacity,
        };
      })
      .filter(notNullish);
    const plateAndJointEntryData = Object.values(objectCapacities).filter(
      ({ objectChecked: { type } }) => type === 'Plate Pair' || type === 'Joint',
    );
    return memberEntryData
      .concat(plateAndJointEntryData)
      .toSorted((a, b) => b.capacity - a.capacity)
      .map((entryData) => ({
        checkResult: null,
        capacity: entryData.capacity,
        mainObject: entryData.objectChecked,
        check: null,
        subObjects: null,
        loadCase: null,
        plateMislocation: null,
      }));
  },
);

const getCheckResultTreeEntriesFromCheckResults = createSelector(
  getCheckResultsForActiveLoadCaseAndItem,
  getSingleSelectedItemEntryOrNull,
  getLoadCasesForActiveComponentDesign,
  getSelectedCheckResultsLoadCase,
  getCheckedMembersByCheckResultForActiveLoadCase,
  (
    checkResults,
    selectedEntry,
    loadCases,
    selectedLoadCase,
    checkedMembersByCheckResult,
  ): readonly CheckResultTreeEntry[] | null => {
    if (!checkedMembersByCheckResult || !loadCases || !checkResults) {
      return null;
    }
    return checkResults.flatMap((checkResult) => {
      const loadCase = selectedLoadCase
        ? null
        : loadCases.find((possibleLoadCase) => possibleLoadCase.guid === checkResult.loadCaseGuid)!;
      const subObjects = checkResult.objects
        .filter((objectChecked) => objectChecked.type !== 'Model' && objectChecked.type !== 'Plate Mislocation')
        .toReversed();
      const plateMislocation =
        checkResult.objects.find((objectChecked) => objectChecked.type === 'Plate Mislocation') || null;

      interface MainObject {
        type: SelectableCheckedObjectType;
        guid: string;
        // An ObjectChecked if this MainObject should have a node in the tree, or null if we are using the selected
        // object as the MainObject and we don't want a node in the tree for it.
        checkedObject: ObjectChecked | null;
      }

      let mainObjects: MainObject[];
      if (isSelectableCheckedObject(selectedEntry)) {
        // We are only showing CheckResults for the selected item, so we make it the MainObject.
        mainObjects = [
          {
            type: selectedEntry.type,
            guid: selectedEntry.item.guid,
            checkedObject: null,
          },
        ];
      } else {
        // We are showing check results for all objects, so we need to specify the top-level object node(s) under
        // which to include this check result.
        const firstCheckedObjectIndex = checkResult.objects.length - 2;
        const firstCheckedObject = checkResult.objects[firstCheckedObjectIndex];
        const firstCheckedObjectType = firstCheckedObject.type;
        switch (firstCheckedObjectType) {
          case 'Joint System': {
            const checkedPlate = checkResult.objects[firstCheckedObjectIndex - 1];
            mainObjects = [{ type: 'platePair', guid: checkedPlate.guid, checkedObject: checkedPlate }];
            break;
          }
          case 'Plate Pair':
            mainObjects = [{ type: 'platePair', guid: firstCheckedObject.guid, checkedObject: firstCheckedObject }];
            break;
          case 'Joint':
            mainObjects = [{ type: 'analogJoint', guid: firstCheckedObject.guid, checkedObject: firstCheckedObject }];
            break;
          default:
            mainObjects = checkedMembersByCheckResult
              .get(checkResult.guid)!
              .map((checkedMember) => ({ type: 'member', guid: checkedMember.guid, checkedObject: checkedMember }));
        }
      }

      return mainObjects.map((mainObject) => ({
        checkResult,
        capacity: checkResult.capacity,
        mainObject: mainObject.checkedObject,
        check: checkResult,
        subObjects: subObjects.filter((objectChecked) => {
          switch (mainObject.type) {
            case 'member':
              return (
                objectChecked.guid !== mainObject.guid &&
                objectChecked.type !== 'Member' &&
                objectChecked.type !== 'Member Stack'
              );
            case 'platePair':
              return objectChecked.type !== 'Plate Pair';
            case 'panel':
              return objectChecked.type === 'Link';
            case 'analogJoint':
            case 'analogLink':
              return false;
            default:
              return assertExhaustiveSwitch(mainObject.type, 'The object type is invalid.');
          }
        }),
        loadCase,
        plateMislocation,
      }));
    });
  },
);

export const getCheckResultTreeEntries = (state: RootState) => {
  const checkResults = getCheckResultsForActiveLoadCaseAndItem(state);
  const selectedLoadCase = getSelectedCheckResultsLoadCase(state);
  const selectedEntry = getSingleSelectedItemEntryOrNull(state);
  const activeAnalysisSet = getActiveAnalysisSet(state);
  const analysisResultsWereSaved = !!(activeAnalysisSet && getAnalysisResultGuids(activeAnalysisSet));
  const useSummary =
    !checkResults && !selectedLoadCase && (!isSelectableCheckedObject(selectedEntry) || !analysisResultsWereSaved);
  return useSummary ? getCheckResultSummaryTreeEntries(state) : getCheckResultTreeEntriesFromCheckResults(state);
};
